import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { EmploymentDetails } from '@kanda-libs/ks-frontend-services';

export interface SecondaryEmploymentFieldsHook {
  employmentStatus: EmploymentDetails['employment_status'] | undefined;
  grossAnnualIncomeName: string;
  onGrossBlur: () => void;
}

export default function useSecondaryEmploymentFields(
  index: number,
  arrayName: string,
): SecondaryEmploymentFieldsHook {
  const { unregister, trigger } = useFormContext();

  const baseFieldName = useMemo(
    () => `${arrayName}.${index}`,
    [arrayName, index],
  );

  const fieldName = useMemo(
    () => `${baseFieldName}.employment_status`,
    [baseFieldName],
  );

  const grossAnnualIncomeName = useMemo(
    () => `${baseFieldName}.gross_annual_income.amount`,
    [baseFieldName],
  );

  const employmentStatus = useWatch({
    name: fieldName,
  });

  const onGrossBlur = useCallback(
    () => trigger(grossAnnualIncomeName),
    [trigger, grossAnnualIncomeName],
  );

  const employmentStatusRef = useRef<
    EmploymentDetails['employment_status'] | undefined
  >();

  const getEmploymentStatusFieldName = useCallback(
    (status: EmploymentDetails['employment_status']): string => {
      if (status === 'employed') return 'employed_details';
      if (status === 'self_employed') return 'self_employed_details';
      if (status === 'not_employed') return 'not_employed_details';
      return 'retired_details';
    },
    [],
  );

  useEffect(() => {
    if (!employmentStatus) return;
    const refStatus = employmentStatusRef.current;
    if (refStatus === undefined) {
      employmentStatusRef.current = employmentStatus;
      return;
    }
    if (employmentStatus === employmentStatusRef.current) return;
    const unregFieldNameSuffix = getEmploymentStatusFieldName(refStatus);
    const unregFieldName = `${baseFieldName}.${unregFieldNameSuffix}`;
    unregister(unregFieldName);
    employmentStatusRef.current = employmentStatus;
  }, [
    baseFieldName,
    getEmploymentStatusFieldName,
    employmentStatus,
    unregister,
  ]);

  return {
    employmentStatus,
    grossAnnualIncomeName,
    onGrossBlur,
  };
}
